import { useState, useCallback, useRef } from 'react';

interface CachedFileData {
  fileId: string;
  headers: string[];
  preview: any[];
  sampleRow: any;
  timestamp: number;
  fileSize: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100 * 1024 * 1024; // 100MB total

export function useFileCache() {
  const cacheRef = useRef<Map<string, CachedFileData>>(new Map());
  const [totalSize, setTotalSize] = useState(0);

  const generateFileId = (file: File): string => {
    return `${file.name}_${file.size}_${file.lastModified}`;
  };

  const setCachedData = useCallback((file: File, data: Partial<CachedFileData>) => {
    const fileId = generateFileId(file);
    const cacheData: CachedFileData = {
      fileId,
      headers: data.headers || [],
      preview: data.preview || [],
      sampleRow: data.sampleRow || {},
      timestamp: Date.now(),
      fileSize: file.size,
    };

    // Evict old entries if needed
    if (totalSize + file.size > MAX_CACHE_SIZE) {
      const sortedEntries = Array.from(cacheRef.current.entries())
        .sort(([, a], [, b]) => a.timestamp - b.timestamp);
      
      let clearedSize = 0;
      for (const [key, value] of sortedEntries) {
        cacheRef.current.delete(key);
        clearedSize += value.fileSize;
        if (totalSize - clearedSize + file.size <= MAX_CACHE_SIZE) break;
      }
      setTotalSize(prev => prev - clearedSize);
    }

    cacheRef.current.set(fileId, cacheData);
    setTotalSize(prev => prev + file.size);
    console.log(`[FileCache] Cached data for ${file.name}, cache size: ${(totalSize + file.size) / 1024 / 1024}MB`);
  }, [totalSize]);

  const getCachedData = useCallback((file: File): CachedFileData | null => {
    const fileId = generateFileId(file);
    const cached = cacheRef.current.get(fileId);
    
    if (!cached) {
      console.log(`[FileCache] Cache miss for ${file.name}`);
      return null;
    }
    
    // Check TTL
    if (Date.now() - cached.timestamp > CACHE_TTL) {
      console.log(`[FileCache] Cache expired for ${file.name}`);
      cacheRef.current.delete(fileId);
      setTotalSize(prev => prev - cached.fileSize);
      return null;
    }
    
    console.log(`[FileCache] Cache hit for ${file.name}`);
    return cached;
  }, []);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
    setTotalSize(0);
    console.log('[FileCache] Cache cleared');
  }, []);

  return { setCachedData, getCachedData, clearCache, totalSize };
}
